import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { PinDayLogo } from '@/components/ui/PinDayLogo'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Globe } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type Language = 'pt' | 'en' | 'es'
type DocumentType = 'privacy' | 'terms' | 'cookies'

interface LegalPageProps {
  type: DocumentType
  initialLanguage?: Language
}

const documentUrls: Record<DocumentType, Record<Language, string>> = {
  privacy: {
    pt: '/legal/privacy-policy.md',
    en: '/legal/en/privacy-policy.md',
    es: '/legal/es/privacy-policy.md',
  },
  terms: {
    pt: '/legal/terms-of-service.md',
    en: '/legal/en/terms-of-service.md',
    es: '/legal/es/terms-of-service.md',
  },
  cookies: {
    pt: '/legal/cookie-policy.md',
    en: '/legal/en/cookie-policy.md',
    es: '/legal/es/cookie-policy.md',
  },
}

const pageTitles: Record<DocumentType, Record<Language, string>> = {
  privacy: {
    pt: 'Política de Privacidade',
    en: 'Privacy Policy',
    es: 'Política de Privacidad',
  },
  terms: {
    pt: 'Termos de Serviço',
    en: 'Terms of Service',
    es: 'Términos de Servicio',
  },
  cookies: {
    pt: 'Política de Cookies',
    en: 'Cookie Policy',
    es: 'Política de Cookies',
  },
}

const backLabel: Record<Language, string> = {
  pt: 'Voltar ao início',
  en: 'Back to home',
  es: 'Volver al inicio',
}

const languageLabels: Record<Language, string> = {
  pt: 'Português',
  en: 'English',
  es: 'Español',
}

// Simple markdown to HTML converter for legal documents
function parseMarkdown(markdown: string): string {
  const html = markdown
    // Escape HTML entities first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    
    // Headers
    .replace(/^######\s+(.*)$/gm, '<h6 class="text-base font-semibold mt-6 mb-2">$1</h6>')
    .replace(/^#####\s+(.*)$/gm, '<h5 class="text-lg font-semibold mt-6 mb-2">$1</h5>')
    .replace(/^####\s+(.*)$/gm, '<h4 class="text-xl font-semibold mt-8 mb-3">$1</h4>')
    .replace(/^###\s+(.*)$/gm, '<h3 class="text-2xl font-semibold mt-10 mb-4">$1</h3>')
    .replace(/^##\s+(.*)$/gm, '<h2 class="text-3xl font-bold mt-12 mb-6 pb-2 border-b border-border">$1</h2>')
    .replace(/^#\s+(.*)$/gm, '<h1 class="text-4xl font-bold mb-8">$1</h1>')
    
    // Bold and italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
    
    // Horizontal rules
    .replace(/^---$/gm, '<hr class="my-8 border-border" />')
    
    // Code blocks (inline)
    .replace(/`([^`]+)`/g, '<code class="px-1.5 py-0.5 bg-muted rounded text-sm font-mono">$1</code>')
    
    // Tables
    .replace(/^\|(.+)\|$/gm, (match, content) => {
      const cells = content.split('|').map((cell: string) => cell.trim())
      const isHeader = cells.some((cell: string) => cell.includes('---'))
      
      if (isHeader) {
        return '' // Skip separator row
      }
      
      const cellTag = 'td'
      const cellHtml = cells
        .map((cell: string) => `<${cellTag} class="border border-border px-4 py-2">${cell}</${cellTag}>`)
        .join('')
      
      return `<tr class="even:bg-muted/50">${cellHtml}</tr>`
    })
    
    // Wrap consecutive table rows
    .replace(/((?:<tr[^>]*>.*?<\/tr>\s*)+)/g, '<table class="w-full border-collapse my-6 text-sm"><tbody>$1</tbody></table>')
    
    // Lists (unordered)
    .replace(/^[-*]\s+(.*)$/gm, '<li class="ml-4">$1</li>')
    .replace(/((?:<li[^>]*>.*?<\/li>\s*)+)/g, '<ul class="list-disc list-inside space-y-1 my-4">$1</ul>')
    
    // Lists (ordered)
    .replace(/^\d+\.\s+(.*)$/gm, '<li class="ml-4">$1</li>')
    
    // Paragraphs (lines that aren't already wrapped)
    .replace(/^(?!<[a-z]|$)(.+)$/gm, '<p class="my-4 leading-relaxed">$1</p>')
    
    // Clean up empty paragraphs
    .replace(/<p class="my-4 leading-relaxed"><\/p>/g, '')
    
    // Fix nested paragraph issues
    .replace(/<p class="my-4 leading-relaxed">(<h[1-6])/g, '$1')
    .replace(/(<\/h[1-6]>)<\/p>/g, '$1')
    .replace(/<p class="my-4 leading-relaxed">(<hr)/g, '$1')
    .replace(/(<hr[^>]*>)<\/p>/g, '$1')
    .replace(/<p class="my-4 leading-relaxed">(<table)/g, '$1')
    .replace(/(<\/table>)<\/p>/g, '$1')
    .replace(/<p class="my-4 leading-relaxed">(<ul)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')

  return html
}

export function LegalPage({ type, initialLanguage = 'pt' }: LegalPageProps) {
  const [content, setContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [language, setLanguage] = useState<Language>(initialLanguage)
  const location = useLocation()

  // Detect language from URL path
  useEffect(() => {
    const path = location.pathname.toLowerCase()
    if (path.startsWith('/es/')) {
      setLanguage('es')
    } else if (path.startsWith('/pt/') || path.includes('/privacidade') || path.includes('/termos') || path.includes('/politica-de-cookies')) {
      setLanguage('pt')
    } else if (path.includes('/privacy') || path.includes('/terms') || path.includes('/tos') || path.includes('/cookies')) {
      setLanguage('en')
    }
  }, [location.pathname])

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true)
      setError(null)
      
      try {
        const url = documentUrls[type][language]
        const response = await fetch(url)
        
        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status}`)
        }
        
        const markdown = await response.text()
        const html = parseMarkdown(markdown)
        setContent(html)
      } catch (err) {
        console.error('Error loading legal document:', err)
        setError(language === 'pt' 
          ? 'Erro ao carregar documento. Por favor, tente novamente.' 
          : 'Error loading document. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchContent()
  }, [type, language])

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage)
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm hidden sm:inline">{backLabel[language]}</span>
            </Link>
            <div className="h-6 w-px bg-border hidden sm:block" />
            <Link to="/">
              <PinDayLogo size="sm" />
            </Link>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Globe className="h-4 w-4" />
                <span className="hidden sm:inline">{languageLabels[language]}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleLanguageChange('pt')}>
                🇧🇷 Português
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleLanguageChange('en')}>
                🇺🇸 English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleLanguageChange('es')}>
                🇪🇸 Español
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {loading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-12 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/4" />
            <div className="h-px bg-border my-8" />
            <div className="space-y-3">
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-full" />
              <div className="h-4 bg-muted rounded w-5/6" />
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              {language === 'pt' ? 'Tentar novamente' : language === 'es' ? 'Intentar de nuevo' : 'Try again'}
            </Button>
          </div>
        ) : (
          <article 
            className="prose prose-neutral dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link to="/">
            <PinDayLogo size="sm" />
          </Link>
          <div className="flex gap-6 text-sm text-muted-foreground">
            <Link 
              to={language === 'pt' ? '/privacidade' : `/${language}/privacy`} 
              className="hover:text-foreground transition-colors"
            >
              {pageTitles.privacy[language]}
            </Link>
            <Link 
              to={language === 'pt' ? '/termos-de-servico' : `/${language}/terms`} 
              className="hover:text-foreground transition-colors"
            >
              {pageTitles.terms[language]}
            </Link>
            <Link
              to={language === 'pt' ? '/politica-de-cookies' : `/${language}/cookies`}
              className="hover:text-foreground transition-colors"
            >
              {pageTitles.cookies[language]}
            </Link>
          </div>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} PinDay
          </p>
        </div>
      </footer>
    </div>
  )
}

// Convenience components for routing
export function PrivacyPage() {
  return <LegalPage type="privacy" />
}

export function TermsPage() {
  return <LegalPage type="terms" />
}

export function CookiePolicyPage() {
  return <LegalPage type="cookies" />
}
